import { Box, Typography } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormsContext } from "../../context/FormsContext";
import { LazyLoading } from "@/components/loaders/Loader/LazyLoading";
import { ErrorModal } from "@dexteel/mesf-core";
import { ModalGridBox, listMenuColumn, modalBoxSx } from "../modal-grid";
import { useSearchUsersFormAsset } from "../../hooks/useSearchUsersFormAsset";
import type { UserFormAssetData } from "../../models/userFormAssetData";
import { DeleteUserFormAssetDialog } from "./components/delete-user-form-asset-modal/delete-user-form-asset-modal";
import { NewAndEditUserFormAssetModal } from "./components/new-user-form-asset-modal/modal-new-and-edit-user-form-asset";
import { INITIAL_VALUES_USER_FORM_ASSET } from "../../models/UserFormAssetInitialValue";
import { EditFormAsset } from "./components/edit-form-asset-modal/edit-form-asset-modal";
import { useSearchFormAsset } from "../../hooks/useSearchFormAsset";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

type Props = {
	FormAssetIdToSearchUserFormAssets: number;
	setOpenNewEditUserFormAsset: (setter: boolean) => void;
	setOpenDeleteUserFormAsset: (setter: boolean) => void;
	setNewUserFormAssetMode: (setter: boolean) => void;
	setShouldUpdateWhenExit: (shouldUpdate: boolean) => void;
	openNewEditUserFormAsset: boolean;
	openDeleteUserFormAsset: boolean;
	newUserFormAssetMode: boolean;
};

export const UsersFormAssetTable = ({
	setShouldUpdateWhenExit,
	FormAssetIdToSearchUserFormAssets,
	setOpenNewEditUserFormAsset,
	openNewEditUserFormAsset,
	openDeleteUserFormAsset,
	setOpenDeleteUserFormAsset,
	newUserFormAssetMode,
	setNewUserFormAssetMode,
}: Props) => {
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [UsersFormAssetColumnDefs, setUsersFormAssetColumnDefs] = useState<
		ColDef[]
	>([]);

	const {
		state: {
			userFormAssetDataSelectedInTable,
			usersFormAssetList,
			FormAsset: { FormAssetId },
			FormAsset,
		},
		actions: {
			setUserFormAssetDataSelectedInTable,
			setUserIdFormAssetDataSelectedInTable,
			setIsDefaultUserFormAssetDataSelectedInTable,
		},
	} = useFormsContext();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>("");
	const searchUsersFormAsset = useSearchUsersFormAsset({
		setIsLoading,
		setError,
		FormAssetIdToSearchUserFormAssets,
	});
	const searchFormAsset = useSearchFormAsset({
		setIsLoading,
		setError,
		FormAssetIdToSearchUserFormAssets,
	});

	const loadingOverlayComponent = useMemo<any>(() => {
		return LazyLoading;
	}, []);

	const openNewUser = () => {
		setUserFormAssetDataSelectedInTable(INITIAL_VALUES_USER_FORM_ASSET);
		setNewUserFormAssetMode(true);
		setOpenNewEditUserFormAsset(true);
	};

	const getUsersFormAssetContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data: UserFormAssetData = params.node?.data;
			const result: (DefaultMenuItem | MenuItemDef)[] = [];
			if (data) {
				result.push({
					name: "Edit User",
					action: () => {
						setNewUserFormAssetMode(false);
						setUserFormAssetDataSelectedInTable(data);
						setUserIdFormAssetDataSelectedInTable(data.UserId);
						setIsDefaultUserFormAssetDataSelectedInTable(data.IsDefault);
						setOpenNewEditUserFormAsset(true);
					},
					icon: '<i class="fas fa-edit"></i>',
				});
			}

			if (data) {
				result.push({
					name: "Add User",
					action: () => {
						setUserFormAssetDataSelectedInTable(INITIAL_VALUES_USER_FORM_ASSET);
						setNewUserFormAssetMode(true);
						setOpenNewEditUserFormAsset(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			if (data) {
				result.push({
					name: "Delete User",
					action: () => {
						setNewUserFormAssetMode(false);
						setOpenDeleteUserFormAsset(true);
						setUserFormAssetDataSelectedInTable(data);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			const addNewUserFormAsset: (DefaultMenuItem | MenuItemDef)[] = [];

			if (!data && FormAssetIdToSearchUserFormAssets !== null) {
				addNewUserFormAsset.push({
					name: "Add User",
					action: () => {
						setUserFormAssetDataSelectedInTable(INITIAL_VALUES_USER_FORM_ASSET);
						setNewUserFormAssetMode(true);
						setOpenNewEditUserFormAsset(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			return data ? result : addNewUserFormAsset;
		},
		[usersFormAssetList],
	);

	const renderColumns = () => {
		setUsersFormAssetColumnDefs([]);
		const newColumns: ColDef[] = [];
		newColumns.push({
			field: "UserName",
			headerName: "User",
			sortable: false,
			flex: 4,
			minWidth: 130,
		});
		newColumns.push({
			field: "IsDefault",
			headerName: "Default",
			sortable: false,
			flex: 1,
			minWidth: 80,
			valueFormatter: (params) => (params.value ? "Yes" : "No"),
		});
		newColumns.push(listMenuColumn);
		setUsersFormAssetColumnDefs(newColumns);
	};

	useEffect(() => {
		renderColumns();
	}, [usersFormAssetList]);

	useEffect(() => {
		if (isLoading) {
			gridApi?.showLoadingOverlay();
		} else gridApi?.hideOverlay();
	}, [isLoading, usersFormAssetList, gridApi]);

	useEffect(() => {
		if (FormAssetIdToSearchUserFormAssets) {
			searchUsersFormAsset();
			searchFormAsset();
		}
	}, [FormAssetIdToSearchUserFormAssets]);
	return (
		<>
			<Grid2 size={{ md: 8, xs: 12 }}>
				{/* Same height as the Assets list beside it (header row + gap + grid) */}
				{isLoading ? (
					<Box
						sx={{
							height: "55vh",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<LazyLoading />
					</Box>
				) : (
					<Box
						sx={{
							height: "55vh",
							display: "flex",
							flexDirection: "column",
							gap: 2,
						}}
					>
						{FormAssetId ? (
							<EditFormAsset FormAsset={FormAsset} />
						) : (
							<Box sx={{ ...modalBoxSx, p: 2 }}>
								<Typography variant="body2" color="text.secondary">
									Select an asset to see its schedule and users
								</Typography>
							</Box>
						)}
						<ModalGridBox
							onAdd={openNewUser}
							addDisabled={!FormAssetIdToSearchUserFormAssets}
						>
							<div style={{ height: "100%", width: "100%" }}>
								<AgGridReact
									rowData={
										FormAssetIdToSearchUserFormAssets ? usersFormAssetList : []
									}
									columnDefs={UsersFormAssetColumnDefs}
									defaultColDef={globalDefaultColDef}
									rowHeight={38}
									headerHeight={42}
									loadingOverlayComponent={loadingOverlayComponent}
									animateRows={true}
									getContextMenuItems={(e: any) =>
										getUsersFormAssetContextMenuItems(e)
									}
									pagination={false}
									onRowDoubleClicked={(event) => {
										setNewUserFormAssetMode(false);
										setUserFormAssetDataSelectedInTable(event.data);
										setOpenNewEditUserFormAsset(true);
									}}
									rowSelection="single"
									suppressCellFocus={true}
									onGridReady={(params) => setGridApi(params.api)}
									overlayNoRowsTemplate={
										FormAssetIdToSearchUserFormAssets
											? "No users"
											: "Select an asset to see its schedule and users"
									}
								/>
							</div>
						</ModalGridBox>
					</Box>
				)}
			</Grid2>
			<NewAndEditUserFormAssetModal
				show={openNewEditUserFormAsset}
				data={userFormAssetDataSelectedInTable}
				FormAssetIdToSearchUserFormAssets={
					FormAssetIdToSearchUserFormAssets as number
				}
				newMode={newUserFormAssetMode}
				setShouldUpdateWhenExit={setShouldUpdateWhenExit}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchUsersFormAsset();
					}
					setOpenNewEditUserFormAsset(false);
				}}
			/>
			<DeleteUserFormAssetDialog
				show={openDeleteUserFormAsset}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchUsersFormAsset();
						setShouldUpdateWhenExit(true);
					}
					setOpenDeleteUserFormAsset(false);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
